// Workflow Detail page with real-time pipeline visualization
import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PlayIcon, PauseIcon, ArchiveIcon, RotateCcw } from "lucide-react";
import { LLMConversationTerminal } from "../components/LLMConversationTerminal";
import { WorkflowPipelineView } from "../components/Workflow/WorkflowPipelineView";
import PipelineVisualization from "../components/PipelineVisualization";
import { ConversationView } from "../components/Workflow/ConversationView";
import { StatusBadge } from "../components/Common/StatusBadge";
import { HumanInputBox } from "../components/HumanInputBox";
import { ArtifactViewer } from "../components/Artifacts";
import { useWebSocket, useWebSocketMessages } from "../hooks/useWebSocket";
import { useWorkflowActions } from "../hooks/useWorkflowActions";
import api from "../services/api";
import { WorkflowStatus } from "../types";
import { formatApiError } from "../utils/errorFormatter";

export const WorkflowDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [workflow, setWorkflow] = useState<WorkflowStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [pipelineName, setPipelineName] = useState<string | null>(null);
  const [pipelineView, setPipelineView] = useState<
    "list" | "visual"
  >("visual"); // Default to visual pipeline view
  const [pipelineSteps, setPipelineSteps] = useState<any[]>([]);
  const [processorMonitorView, setProcessorMonitorView] = useState<
    "detailed" | "chat"
  >("chat"); // Default to chat view for processor monitor

  const { isConnected } = useWebSocket();
  const {
    toggleWorkflow,
    archiveWorkflow,
    loading: actionLoading,
  } = useWorkflowActions(
    () => {
      fetchWorkflow();
      setRefreshTrigger((prev) => prev + 1);
    },
    (workflowId, status) => {
      // Optimistically update the workflow status in UI
      if (workflow && workflow.id.toString() === workflowId) {
        setWorkflow({ 
          ...workflow, 
          status: status as "running" | "paused" | "completed" | "failed" | "waiting_approval"
        });
      }
    }
  );

  const isWorkflowLoading = id ? actionLoading[id] : false;

  const fetchWorkflow = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await api.getWorkflowDetail(id);
      setWorkflow(data);

      // Fetch pipeline name and steps if workflow has a pipeline_id
      if (data.pipeline_id) {
        try {
          const pipelineData = await api.getPipelineSteps(data.pipeline_id);
          setPipelineName(pipelineData.name);
          setPipelineSteps(pipelineData.steps || []);
        } catch (error) {
          console.error("Failed to fetch pipeline data:", error);
        }
      }
    } catch (error) {
      console.error("Failed to fetch workflow:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  // Listen for workflow updates
  useWebSocketMessages(
    "workflow_update",
    useCallback(
      (data: any) => {
        if (data.workflow_id === parseInt(id || "0")) {
          console.log("[DEBUG] WorkflowDetail received workflow_update:", data);
          // Update workflow status inline without full page refresh (SWRM-164)
          setWorkflow((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              status: data.status || prev.status,
            };
          });
        }
      },
      [id]
    )
  );

  // Listen for agent execution updates
  useWebSocketMessages(
    "agent_execution_update",
    useCallback(
      (data: any) => {
        if (data.workflow_id === parseInt(id || "0")) {
          console.log(
            "[DEBUG] WorkflowDetail received agent_execution_update:",
            data
          );
          setRefreshTrigger((prev) => prev + 1);
        }
      },
      [id]
    )
  );

  // Listen for workflow paused events (SWRM-111)
  useWebSocketMessages(
    "workflow_paused",
    useCallback(
      (data: any) => {
        if (data.workflow_id === parseInt(id || "0")) {
          console.log("[DEBUG] WorkflowDetail received workflow_paused:", data);
          // Just update the workflow status inline without full page refresh (SWRM-164)
          setWorkflow((prev) => 
            prev ? { ...prev, status: data.status || "waiting_approval" } : prev
          );
        }
      },
      [id]
    )
  );

  // Handle submitting human input (SWRM-111)
  const handleSubmitHumanInput = async (content: string) => {
    if (!workflow || !id) return;

    try {
      console.log("[DEBUG] Submitting human input:", content);

      // Submit the human input
      await api.submitHumanInput(id, content);

      console.log("[DEBUG] Human input submitted successfully");

      // Automatically resume the workflow to continue with the human input (SWRM-111)
      console.log("[DEBUG] Automatically resuming workflow...");
      await api.resumeWorkflow(id);
      console.log("[DEBUG] Workflow resumed successfully");

      // WebSocket will handle updating the conversation in real-time (SWRM-164)
      // No need to refresh the page - let the real-time updates handle it
    } catch (error: any) {
      console.error("Failed to submit human input or resume workflow:", error);
      throw new Error(formatApiError(error));
    }
  };

  const handleResetWorkflow = async () => {
    if (!workflow) return;

    const message =
      `⚠️ Are you sure you want to RESET workflow "${workflow.name}"?\n\n` +
      "This will DELETE:\n" +
      "• All artifacts and execution data\n" +
      "• All LLM conversations\n" +
      "• All tasks and tickets\n" +
      "• The workspace directory (if created)\n\n" +
      "The workflow will be reset to its initial state.\n\n" +
      "This action CANNOT be undone!";

    if (window.confirm(message)) {
      try {
        // Use fetch API to call reset endpoint
        const token = localStorage.getItem("auth_token");
        const response = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:8000"
          }/api/workflows/${workflow.id}/reset`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to reset workflow");
        }

        const data = await response.json();
        console.log("Workflow reset:", data);

        // Show success message
        const statusMsg = data.status_updated
          ? `\n\nWorkflow status: ${data.new_status}`
          : "\n\n⚠️ Warning: Status update may have failed (check errors)";

        alert(
          `Workflow reset successfully!\n\nDeleted:\n` +
            `• ${data.artifacts_deleted} artifacts\n` +
            `• ${data.executions_deleted} executions\n` +
            `• ${data.conversations_deleted} conversations\n` +
            `• ${data.tasks_deleted} tasks\n` +
            `• ${data.tickets_deleted} tickets\n` +
            `${data.workspace_deleted ? "• Workspace directory\n" : ""}` +
            `\nRegenerated:\n` +
            `• ${data.artifacts_regenerated} input artifacts\n` +
            statusMsg +
            (data.errors && data.errors.length > 0
              ? `\n\n⚠️ Errors:\n${data.errors.join("\n")}`
              : "")
        );

        // Refresh workflow data
        setTimeout(() => {
          fetchWorkflow();
          setRefreshTrigger((prev) => prev + 1);
        }, 500);
      } catch (error: any) {
        console.error("Failed to reset workflow:", error);
        alert(`Failed to reset workflow: ${error.message}`);
      }
    }
  };

  if (loading || !workflow) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">
            Loading workflow...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {workflow.name || `Workflow ${workflow.id}`}
              </h1>
              <StatusBadge status={workflow.status} />
            </div>
            {pipelineName && (
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Pipeline: <span className="font-medium">{pipelineName}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <div
                className={`w-2 h-2 rounded-full ${
                  isConnected ? "bg-green-500" : "bg-gray-400"
                }`}
              />
              <span>{isConnected ? "Live" : "Offline"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Visualization */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Workflow Pipeline
            </h2>
            {/* View Toggle Tabs - Visual and List only */}
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => setPipelineView("visual")}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                  pipelineView === "visual"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => setPipelineView("list")}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${
                  pipelineView === "list"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
              >
                List
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {workflow.status === "running" ||
            workflow.status === "paused" ||
            workflow.status === "waiting_approval" ? (
              <button
                onClick={() => toggleWorkflow(workflow.id, workflow.status)}
                disabled={isWorkflowLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {workflow.status === "running" ? (
                  <>
                    <PauseIcon className="h-4 w-4 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Resume
                  </>
                )}
              </button>
            ) : null}
            <button
              onClick={handleResetWorkflow}
              disabled={isWorkflowLoading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-md hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-600 dark:hover:bg-yellow-900/30"
              title="Reset workflow (delete all data and start from scratch)"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </button>
            <button
              onClick={() => archiveWorkflow(workflow.id)}
              disabled={isWorkflowLoading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              <ArchiveIcon className="h-4 w-4 mr-2" />
              Archive
            </button>
          </div>
        </div>

        {/* Conditional rendering based on view toggle */}
        {pipelineView === "list" && id && (
          <WorkflowPipelineView
            workflowId={id}
            pipelineId={workflow.pipeline_id}
            refreshTrigger={refreshTrigger}
            workflowStatus={workflow.status}
          />
        )}

        {pipelineView === "visual" &&
          id &&
          pipelineSteps.length > 0 &&
          workflow.pipeline_id && (
            <PipelineVisualization
              workflowId={parseInt(id)}
              pipelineId={workflow.pipeline_id}
              pipelineSteps={pipelineSteps}
              workflowStatus={workflow.status}
            />
          )}

        {pipelineView === "visual" && pipelineSteps.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading pipeline visualization...
          </div>
        )}
      </div>

      {/* Processor Monitor with Detailed/Chat tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Processor Monitor
            </h2>
            {/* View Toggle Tabs - Detailed and Chat */}
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => setProcessorMonitorView("detailed")}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                  processorMonitorView === "detailed"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
              >
                Detailed
              </button>
              <button
                type="button"
                onClick={() => setProcessorMonitorView("chat")}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${
                  processorMonitorView === "chat"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
              >
                Chat
              </button>
            </div>
          </div>
        </div>

        {/* Conditional rendering based on processor monitor view */}
        {processorMonitorView === "detailed" && id && (
          <div style={{ height: "600px" }}>
            <LLMConversationTerminal workflowId={id} />
          </div>
        )}

        {processorMonitorView === "chat" && id && (
          <ConversationView workflowId={id} workflowStatus={workflow.status} />
        )}
      </div>

      {/* Human Input Box - Show when agent requested input (SWRM-111) */}
      {/* Hide when Chat view is active since it has its own input (SWRM-164) */}
      {processorMonitorView !== "chat" &&
        (workflow.status === "paused" ||
          workflow.status === "waiting_approval") &&
        workflow.awaiting_human_input &&
        id && (
          <div>
            <HumanInputBox workflowId={id} onSubmit={handleSubmitHumanInput} />
          </div>
        )}

      {/* Artifact Viewer */}
      {id && <ArtifactViewer workflowId={id} workflow={workflow} />}
    </div>
  );
};
